// render/insignia.ts — Canvas drawing functions for military rank insignia

export function getStackedWidth(count: number): number {
  const iconSize = 12;
  const iconSpacing = 3;

  if (count <= 3) {
    return count * iconSize + (count - 1) * iconSpacing;
  } else if (count === 4) {
    return 2 * iconSize + iconSpacing;
  } else {
    const topCount = 3;
    const bottomCount = count - 3;
    const topWidth = topCount * iconSize + (topCount - 1) * iconSpacing;
    const bottomWidth =
      bottomCount * iconSize + (bottomCount - 1) * iconSpacing;
    return Math.max(topWidth, bottomWidth);
  }
}

export function drawStackedInsignia(
  ctx: CanvasRenderingContext2D,
  startX: number,
  centerY: number,
  count: number,
  type: string,
): number {
  const iconSize = 12;
  const iconSpacing = 3;
  const rowSpacing = 10;

  const positions: { x: number; y: number }[] = [];

  if (count <= 3) {
    for (let i = 0; i < count; i++) {
      positions.push({
        x: startX + i * (iconSize + iconSpacing),
        y: centerY,
      });
    }
  } else if (count === 4) {
    // Slight offset for authentic look
    const offsetX = iconSize / 3;

    for (let i = 0; i < 2; i++) {
      positions.push({
        x: startX + i * (iconSize + iconSpacing),
        y: centerY - rowSpacing / 2,
      });
    }

    for (let i = 0; i < 2; i++) {
      positions.push({
        x: startX + offsetX + i * (iconSize + iconSpacing),
        y: centerY + rowSpacing / 2,
      });
    }
  } else {
    const topWidth = 3 * iconSize + 2 * iconSpacing;

    for (let i = 0; i < 3; i++) {
      positions.push({
        x: startX + i * (iconSize + iconSpacing),
        y: centerY - rowSpacing / 2,
      });
    }

    const bottomCount = count - 3;
    const bottomWidth =
      bottomCount * iconSize + (bottomCount - 1) * iconSpacing;
    const bottomStartX = startX + (topWidth - bottomWidth) / 2;
    for (let i = 0; i < bottomCount; i++) {
      positions.push({
        x: bottomStartX + i * (iconSize + iconSpacing),
        y: centerY + rowSpacing / 2,
      });
    }
  }

  for (const pos of positions) {
    if (type === "eagle") {
      drawEagle(ctx, pos.x + iconSize / 2, pos.y, iconSize / 2 - 1);
    } else if (type === "star") {
      drawStar(ctx, pos.x + iconSize / 2, pos.y, iconSize / 2 - 1);
    } else if (type === "bar") {
      ctx.fillRect(pos.x, pos.y - 3, iconSize, 6);
    } else if (type === "chevron") {
      drawChevron(ctx, pos.x + iconSize / 2, pos.y, 0);
    }
  }

  return getStackedWidth(count);
}

export function drawEagle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
) {
  ctx.save();
  ctx.translate(x, y);

  ctx.beginPath();
  ctx.moveTo(-size, 0);
  ctx.quadraticCurveTo(-size * 0.7, -size * 0.6, -size * 0.2, -size * 0.2);
  ctx.lineTo(0, -size * 0.3);
  ctx.lineTo(0, size * 0.4);
  ctx.lineTo(size * 0.2, -size * 0.2);
  ctx.quadraticCurveTo(size * 0.7, -size * 0.6, size, 0);
  ctx.quadraticCurveTo(size * 0.5, size * 0.3, 0, size * 0.4);
  ctx.quadraticCurveTo(-size * 0.5, size * 0.3, -size, 0);

  ctx.fill();
  ctx.restore();
}

export function drawChevron(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rotation: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.moveTo(-4, 3);
  ctx.lineTo(0, -3);
  ctx.lineTo(4, 3);
  ctx.stroke();
  ctx.restore();
}

export function drawStar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const angle = (i * 4 * Math.PI) / 5;
    const px = Math.cos(angle) * radius;
    const py = Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function drawRankInsignia(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  y: number,
  pellets: number,
  _isMe: boolean,
) {
  if (pellets < 20) return;

  ctx.lineWidth = 2;

  // Calculate insignia counts - delayed combination to show 5 before combining
  const eagles = Math.floor(pellets / 3000);
  const remainingAfterEagles = pellets % 3000;
  const stars = Math.floor(remainingAfterEagles / 600);
  const remainingAfterStars = remainingAfterEagles % 600;
  const bars = Math.floor(remainingAfterStars / 120);
  const remainingAfterBars = remainingAfterStars % 120;
  const chevrons = Math.floor(remainingAfterBars / 20);

  let totalWidth = 0;
  const typeSpacing = 12;

  if (eagles > 0) totalWidth += getStackedWidth(Math.min(eagles, 5));
  if (stars > 0) {
    totalWidth +=
      (totalWidth > 0 ? typeSpacing : 0) + getStackedWidth(Math.min(stars, 5));
  }
  if (bars > 0) {
    totalWidth +=
      (totalWidth > 0 ? typeSpacing : 0) + getStackedWidth(Math.min(bars, 5));
  }
  if (chevrons > 0) {
    totalWidth +=
      (totalWidth > 0 ? typeSpacing : 0) +
      getStackedWidth(Math.min(chevrons, 5));
  }

  let currentX = centerX - totalWidth / 2;

  if (eagles > 0) {
    ctx.fillStyle = "#e5e7eb";
    ctx.strokeStyle = "#e5e7eb";
    currentX +=
      drawStackedInsignia(ctx, currentX, y, Math.min(eagles, 5), "eagle") +
      typeSpacing;
  }

  if (stars > 0) {
    ctx.fillStyle = "#fbbf24";
    ctx.strokeStyle = "#fbbf24";
    currentX +=
      drawStackedInsignia(ctx, currentX, y, Math.min(stars, 5), "star") +
      typeSpacing;
  }

  if (bars > 0) {
    ctx.fillStyle = "#94a3b8";
    ctx.strokeStyle = "#94a3b8";
    currentX +=
      drawStackedInsignia(ctx, currentX, y, Math.min(bars, 5), "bar") +
      typeSpacing;
  }

  if (chevrons > 0) {
    ctx.fillStyle = "#d97706";
    ctx.strokeStyle = "#d97706";
    drawStackedInsignia(ctx, currentX, y, Math.min(chevrons, 5), "chevron");
  }
}
